/**
 * ProfileEnquiries — "Profile Enquiries & Invitations".
 *
 * Two simple, read-and-respond sections for a carer holding either
 * Carer Profile Membership or Pro Carer Circle — NOT a messaging inbox.
 * Each enquiry/invitation supports exactly one response action; there
 * is no reply thread anywhere on this page. Progression beyond a
 * carer's response happens entirely on the family's side via the
 * existing Care Room invite journey (Browse Carer Profiles / Care
 * Needs), which this page deliberately does not duplicate.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, Loader2, MapPin, CalendarClock, Inbox, Send, ThumbsUp, ThumbsDown,
  HelpCircle, X, CheckCircle2,
} from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { useHandoverSession } from "../hooks/useHandoverSession";

const ENQUIRY_STATUS_LABELS = {
  submitted: "Awaiting your response",
  interested: "You're interested",
  declined: "Declined",
  more_info_requested: "You asked for more information",
};
const INVITE_STATUS_LABELS = {
  sent: "Awaiting your response",
  dismissed: "Dismissed",
  converted: "You expressed interest",
};

function StatusPill({ status, labels }) {
  const cls = status === "submitted" || status === "sent"
    ? "bg-amber-100 text-amber-800"
    : status === "declined" || status === "dismissed"
    ? "bg-slate-100 text-ink-muted"
    : "bg-teal/10 text-teal";
  return <span className={`pill text-xs font-semibold ${cls}`}>{labels[status] || status}</span>;
}

function EnquiryCard({ enquiry, onRespond, busy }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid={`enquiry-card-${enquiry.enquiry_id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="font-display font-semibold" data-testid={`enquiry-title-${enquiry.enquiry_id}`}>{enquiry.title}</div>
          <div className="mt-0.5 text-xs text-ink-muted">From {enquiry.family_name}</div>
        </div>
        <StatusPill status={enquiry.status} labels={ENQUIRY_STATUS_LABELS} />
      </div>
      <div className="mt-3 grid sm:grid-cols-2 gap-2 text-sm text-ink-muted">
        <div className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" /> {enquiry.general_location}</div>
        <div>{enquiry.care_type}</div>
        <div>{enquiry.work_pattern}</div>
        {enquiry.start_date && <div className="flex items-center gap-1.5"><CalendarClock className="w-3.5 h-3.5" /> {enquiry.start_date}</div>}
      </div>
      <p className="mt-3 text-sm text-ink whitespace-pre-wrap">{enquiry.message}</p>
      {enquiry.status === "submitted" ? (
        <div className="mt-4 flex flex-wrap gap-2">
          <button type="button" disabled={busy} onClick={() => onRespond(enquiry.enquiry_id, "interested")}
                  className="btn-teal text-sm" data-testid={`enquiry-interested-btn-${enquiry.enquiry_id}`}>
            <ThumbsUp className="w-4 h-4" /> Interested
          </button>
          <button type="button" disabled={busy} onClick={() => onRespond(enquiry.enquiry_id, "more_info")}
                  className="btn-outline text-sm" data-testid={`enquiry-more-info-btn-${enquiry.enquiry_id}`}>
            <HelpCircle className="w-4 h-4" /> Ask for more information
          </button>
          <button type="button" disabled={busy} onClick={() => onRespond(enquiry.enquiry_id, "decline")}
                  className="text-sm text-ink-muted hover:text-ink inline-flex items-center gap-1.5 px-3" data-testid={`enquiry-decline-btn-${enquiry.enquiry_id}`}>
            <ThumbsDown className="w-4 h-4" /> Decline
          </button>
        </div>
      ) : (
        <p className="mt-3 text-xs text-ink-soft">
          {enquiry.status === "interested" && "The family can now invite you to a Care Discussion Room to take things further — no contact details have been shared automatically."}
          {enquiry.status === "more_info_requested" && "The family has been told you'd like more information before deciding."}
          {enquiry.status === "declined" && "You declined this enquiry."}
        </p>
      )}
    </div>
  );
}

function InvitationCard({ invitation, onRespond, busy }) {
  const cn = invitation.care_need;
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid={`invitation-card-${invitation.invitation_id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="font-display font-semibold">
          {cn?.care_type_label || "Care Need"} {cn?.location_area ? `in ${cn.location_area}` : ""}
        </div>
        <StatusPill status={invitation.status} labels={INVITE_STATUS_LABELS} />
      </div>
      {cn && (
        <div className="mt-3 grid sm:grid-cols-2 gap-2 text-sm text-ink-muted">
          <div className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" /> {cn.location_area || "Not specified"}</div>
          <div>{cn.rota_pattern || cn.hours_per_week_band || "Work pattern not specified"}</div>
          <div className="flex items-center gap-1.5"><CalendarClock className="w-3.5 h-3.5" /> {cn.start_date || cn.preferred_start_timing || "Start date not specified"}</div>
        </div>
      )}
      {invitation.status === "sent" ? (
        <div className="mt-4 flex flex-wrap gap-2">
          <button type="button" disabled={busy} onClick={() => onRespond(invitation.invitation_id, "express-interest")}
                  className="btn-teal text-sm" data-testid={`invitation-express-interest-btn-${invitation.invitation_id}`}>
            <Send className="w-4 h-4" /> I'm interested
          </button>
          <button type="button" disabled={busy} onClick={() => onRespond(invitation.invitation_id, "dismiss")}
                  className="text-sm text-ink-muted hover:text-ink inline-flex items-center gap-1.5 px-3" data-testid={`invitation-dismiss-btn-${invitation.invitation_id}`}>
            <X className="w-4 h-4" /> Dismiss
          </button>
        </div>
      ) : (
        <p className="mt-3 text-xs text-ink-soft">
          {invitation.status === "converted" && "You've expressed interest in this Care Need — the family can see this on their side."}
          {invitation.status === "dismissed" && "You dismissed this invitation."}
        </p>
      )}
    </div>
  );
}

export default function ProfileEnquiries() {
  const { entitlements } = useHandoverSession();
  const isPro = entitlements.includes("pro_carer");
  const [enquiries, setEnquiries] = useState(null);
  const [invitations, setInvitations] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    try {
      const [{ data: e }, { data: i }] = await Promise.all([
        api.get("/api/carer-profile/mine/enquiries"),
        api.get("/api/care-needs/invitations/mine"),
      ]);
      setEnquiries(e.enquiries || []);
      setInvitations(i.invitations || []);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Could not load your enquiries.");
    }
  };

  useEffect(() => { load(); }, []);

  const respondToEnquiry = async (enquiryId, action) => {
    setBusyId(enquiryId);
    try {
      await api.post(`/api/carer-profile/mine/enquiries/${enquiryId}/respond`, { action });
      toast.success(action === "interested" ? "Marked as interested." : action === "decline" ? "Enquiry declined." : "Asked for more information.");
      await load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Could not update this enquiry.");
    } finally { setBusyId(null); }
  };

  const respondToInvitation = async (invitationId, action) => {
    setBusyId(invitationId);
    try {
      if (action === "dismiss") {
        await api.post(`/api/care-needs/invitations/${invitationId}/dismiss`);
        toast.success("Invitation dismissed.");
      } else {
        await api.post(`/api/care-needs/invitations/${invitationId}/express-interest`);
        toast.success("You've expressed interest in this Care Need.");
      }
      await load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Could not update this invitation.");
    } finally { setBusyId(null); }
  };

  const loading = enquiries === null || invitations === null;
  const backHref = isPro ? "/workspace/pro-carer-circle" : "/workspace/carer-starter";

  return (
    <div className="min-h-screen bg-surface-carer" data-testid="page-profile-enquiries">
      <div className="container-jcc py-8 md:py-14 max-w-3xl">
        <Link to={backHref} className="inline-flex items-center gap-2 text-sm text-ink-muted hover:text-ink" data-testid="enquiries-back">
          <ArrowLeft className="w-4 h-4" /> Back to my hub
        </Link>
        <div className="overline text-purple mt-6">Carer Profile</div>
        <h1 className="mt-2 font-display font-bold text-3xl md:text-4xl">Profile Enquiries & Invitations</h1>
        <p className="mt-3 text-ink-muted max-w-2xl leading-relaxed">
          Private enquiries families have sent to your Carer Profile, and Care Needs you've been invited to view.
          Each one takes a single response — there's no back-and-forth messaging here. If you say you're interested,
          the family can invite you to a Care Discussion Room to take things further.
        </p>

        {loading ? (
          <div className="mt-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-purple" /></div>
        ) : (
          <div className="mt-8 space-y-10">
            <section>
              <h2 className="font-display font-semibold text-lg flex items-center gap-2">
                <Inbox className="w-5 h-5 text-purple" /> Profile enquiries
              </h2>
              {enquiries.length === 0 ? (
                <p className="mt-3 text-sm text-ink-muted" data-testid="enquiries-empty">No enquiries yet.</p>
              ) : (
                <div className="mt-4 space-y-3">
                  {enquiries.map((e) => (
                    <EnquiryCard key={e.enquiry_id} enquiry={e} busy={busyId === e.enquiry_id} onRespond={respondToEnquiry} />
                  ))}
                </div>
              )}
            </section>

            <section>
              <h2 className="font-display font-semibold text-lg flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-purple" /> Care Need invitations
              </h2>
              {invitations.length === 0 ? (
                <p className="mt-3 text-sm text-ink-muted" data-testid="invitations-empty">No Care Need invitations yet.</p>
              ) : (
                <div className="mt-4 space-y-3">
                  {invitations.map((i) => (
                    <InvitationCard key={i.invitation_id} invitation={i} busy={busyId === i.invitation_id} onRespond={respondToInvitation} />
                  ))}
                </div>
              )}
            </section>
          </div>
        )}
      </div>
    </div>
  );
}
